import { InfoCircle, LampOn, Danger } from '@/components/icons';
import { Heading, Text } from '@/components/system';
import { cn } from '@/lib/utils';
import type { HelpArticleBlock, HelpCalloutTone } from '../types';
import type { ReactNode } from 'react';

function renderInline(text: string): ReactNode[] {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return (
                <strong key={index} className="font-semibold text-foreground">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        return part;
    });
}

const CALLOUT_STYLES: Record<
    HelpCalloutTone,
    { container: string; icon: typeof InfoCircle; iconClassName: string; defaultTitle: string }
> = {
    info: {
        container: 'border-info/25 bg-info-subtle/60',
        icon: InfoCircle,
        iconClassName: 'text-info',
        defaultTitle: 'Bom saber',
    },
    success: {
        container: 'border-success/25 bg-success-subtle/60',
        icon: LampOn,
        iconClassName: 'text-success',
        defaultTitle: 'Dica',
    },
    warning: {
        container: 'border-warning/30 bg-warning-subtle/60',
        icon: Danger,
        iconClassName: 'text-warning',
        defaultTitle: 'Atenção',
    },
};

function ArticleBlock({ block }: { block: HelpArticleBlock }) {
    switch (block.type) {
        case 'paragraph':
            return (
                <Text variant="body" className="text-[0.95rem] text-foreground/90">
                    {renderInline(block.text)}
                </Text>
            );

        case 'subheading':
            return (
                <Heading
                    as="h2"
                    variant="subsection"
                    id={block.id}
                    className="scroll-mt-24 pt-4 first:pt-0"
                >
                    {block.text}
                </Heading>
            );

        case 'steps':
            return (
                <ol className="space-y-3">
                    {block.items.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-xs font-bold tabular-nums text-brand">
                                {index + 1}
                            </span>
                            <Text variant="body" className="text-[0.95rem] text-foreground/90">
                                {renderInline(item)}
                            </Text>
                        </li>
                    ))}
                </ol>
            );

        case 'list':
            return (
                <ul className="space-y-2">
                    {block.items.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                            <span
                                aria-hidden="true"
                                className="mt-2 size-1.5 shrink-0 rounded-full bg-brand/60"
                            />
                            <Text variant="body" className="text-[0.95rem] text-foreground/90">
                                {renderInline(item)}
                            </Text>
                        </li>
                    ))}
                </ul>
            );

        case 'callout': {
            const style = CALLOUT_STYLES[block.tone];
            const Icon = style.icon;

            return (
                <div className={cn('flex gap-3 rounded-xl border p-4', style.container)}>
                    <Icon className={cn('mt-0.5 size-5 shrink-0', style.iconClassName)} />
                    <div className="min-w-0 space-y-1">
                        <p className="text-sm font-semibold text-foreground">
                            {block.title ?? style.defaultTitle}
                        </p>
                        <Text variant="body" className="text-foreground/85">
                            {renderInline(block.text)}
                        </Text>
                    </div>
                </div>
            );
        }

        case 'image':
            return (
                <figure className="space-y-2">
                    <img
                        src={block.src}
                        alt={block.alt}
                        loading="lazy"
                        className="w-full rounded-xl border border-border/80 shadow-elevation-1"
                    />
                    {block.caption && (
                        <figcaption>
                            <Text variant="caption" className="text-center">
                                {block.caption}
                            </Text>
                        </figcaption>
                    )}
                </figure>
            );
    }
}

type ArticleBlocksProps = {
    blocks: HelpArticleBlock[];
};

export function ArticleBlocks({ blocks }: ArticleBlocksProps) {
    return (
        <div className="space-y-5">
            {blocks.map((block, index) => (
                <ArticleBlock key={index} block={block} />
            ))}
        </div>
    );
}
